import { apiFailureReportContext, isUnansweredRequest } from '@flashka/shared/api';
import { isExpectedUploadError } from '@flashka/shared/upload/transfer';

import { HttpStatusError } from './api/retry-policy';

/**
 * A mutation's own say in whether its failure is worth a Sentry event:
 * `meta: { skipSentry: true }` opts it out entirely, and a predicate opts out
 * only the failures that mutation knows to be expected — a declined card, a
 * plan already changed — while its genuine failures still report.
 */
export type SkipSentry = boolean | ((error: unknown) => boolean);

declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: Record<string, unknown> & { skipSentry?: SkipSentry };
  }
}

/**
 * Expected, flow-control mutation failures that must not be reported to
 * Sentry:
 *   - requests that were never answered — aborted (user navigated away /
 *     query cancelled) or lost at the transport level (flaky wifi, captive
 *     portals, server restarts — fired constantly by the debounced study-time
 *     sync mutation on any unstable connection),
 *   - uploads the USER stopped, lost, stalled or sized over the limit — the
 *     upload toast already says so and offers Retry (`isExpectedUploadError`,
 *     shared with mobile),
 *   - "Credits finished" API rejections, which the app handles as normal
 *     flow by opening the payment dialog (the API encodes them as a
 *     JSON-stringified `error.message`).
 */
export const isExpectedMutationError = (error: unknown): boolean => {
  if (isUnansweredRequest(error) || isExpectedUploadError(error)) {
    return true;
  }
  if (error instanceof Error) {
    try {
      const data: unknown = JSON.parse(error.message);
      if (
        typeof data === 'object' &&
        data !== null &&
        'message' in data &&
        (data as { message: unknown }).message === 'Credits finished'
      ) {
        return true;
      }
    } catch {
      // error.message is not a JSON-encoded API error — fall through.
    }
  }
  return false;
};

/** Whether the central MutationCache handler should capture this failure. */
export function shouldReportMutationError(
  error: unknown,
  meta: { skipSentry?: SkipSentry } | undefined,
): boolean {
  const skip = meta?.skipSentry;
  if (skip === true) return false;
  if (typeof skip === 'function' && skip(error)) return false;
  return !isExpectedMutationError(error);
}

/**
 * Tags and context for a failed `/api` answer, so its Sentry event says which
 * refusal it was (`api.reason`) and where the server's side is: `api.trace_id`
 * is the trace the API's error events and log lines carry — the page's own
 * trace when the request propagated it, and searchable either way.
 */
export function httpErrorReportContext(error: unknown):
  | {
      tags: Record<string, string>;
      contexts: { api: { status: number } & HttpStatusError['details'] };
    }
  | undefined {
  if (!(error instanceof HttpStatusError)) return undefined;
  const { tags, contexts } = apiFailureReportContext(error.status, error.details);
  return {
    tags: { 'http.status_code': String(error.status), ...tags },
    contexts: { api: { ...contexts.api, status: error.status } },
  };
}
